import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Meta } from '@angular/platform-browser';
import { Location } from '@angular/common';
import { Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { PlatformService } from '../services/platform.service';

export interface Locale {
  lang: string;
  data: any;
}

@Injectable({
  providedIn: 'root',
})

export class TranslationService {
  // Private properties
  private langIds: any = [];

  constructor(
    private translate: TranslateService,
    private router: Router,
    private meta: Meta,
    private location: Location,
    @Inject(DOCUMENT) private document: Document,
    public platformService: PlatformService
  ) {
    // add new langIds to the list
    this.translate.addLangs([environment.languages.default]);

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang(environment.languages.default);
  }

  loadTranslations(...args: Locale[]): void {
    const locales = [...args];

    locales.forEach((locale) => {
      // use setTranslation() with the third argument set to true
      // to append translations instead of replacing them
      this.translate.setTranslation(locale.lang, locale.data, true);

      this.langIds.push(locale.lang);
    });

    // add new languages to the list
    this.translate.addLangs(this.langIds);
  }

  setLanguageLanding() {
    const savedLang = this.platformService.isServer ? "" : localStorage.getItem(environment.languages.saveKey);
    const defaultLang = this.translate.getDefaultLang();
    const currentUrl = this.location.path();
    const arrayOfUrlParts = currentUrl.indexOf("/") !== -1 ? currentUrl.split("/") || [] : currentUrl ? [currentUrl] : [];
    const isEnInURL = arrayOfUrlParts.includes("en");
    const isArInURL = arrayOfUrlParts.includes("ar");

    if (isEnInURL || isArInURL) {
      const lang = isEnInURL ? 'en' : 'ar';
      // enhance it by specifing the index of the language..
      this.translate.use(lang);
      this.setHtmlDirection(lang);
      if (this.platformService.isClient()) localStorage.setItem(environment.languages.saveKey, lang);
      return false;
    } else {
      const lang = savedLang || defaultLang; //in-order
      this.translate.use(lang);
      this.setHtmlDirection(lang);
      if (this.platformService.isClient()) localStorage.setItem(environment.languages.saveKey, lang);
      let finalURL = `/${lang}${currentUrl}`;
      if (
        (finalURL.indexOf("/en/en") !== -1 || finalURL.indexOf("/ar/ar") !== -1) ||
        (finalURL.indexOf("/en/ar") !== -1 || finalURL.indexOf("/ar/en") !== -1)
      ) {
        finalURL = `${currentUrl}`;
      }
      return finalURL;
    }
  }

  setLanguage(newLang: string) {
    if (newLang) {
      const oldLang = newLang === 'en' ? 'ar' : 'en';
      let currentUrl = this.location.path();
      this.translate.use(newLang);
      this.router.navigateByUrl(currentUrl.replace(`/${oldLang}`, `/${newLang}`));
      this.setHtmlDirection(newLang);
      // setTimeout(() => {
      //   if (typeof window !== 'undefined') window.location.reload();
      // });
    }
  }


  setLanguageBackBTN(newLang: string) {
    this.translate.use(newLang);
    this.setHtmlDirection(newLang);
  }

  setHtmlDirection(lang: string) {
    if (typeof window !== "undefined") {
      this.document.documentElement.lang = lang;
      this.document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
      this.document.documentElement.classList.add(lang === "ar" ? "rtl" : "ltr");
      this.document.documentElement.classList.remove(lang !== "ar" ? "rtl" : "ltr");
    }

    this.meta.updateTag({ property: 'og:locale', content: `${lang}_${lang === "ar" ? "rtl" : "ltr"}` }, 'property="og:locale"');
    this.meta.updateTag({ property: 'og:locale:alternate', content: lang === "ar" ? "en_ltr" : "ar_rtl" }, 'property="og:locale:alternate"');
  }

  /**
   * Returns selected language
   */
  private readonly ALLOWED_LANGS = ['en', 'ar'];
  
  private getSafeLang(lang: any): string {
    if (typeof lang === 'string' && this.ALLOWED_LANGS.includes(lang)) {
      return lang;
    }
    return this.translate.getDefaultLang() || 'en';
  }


  getSelectedLanguage(): any {
    const rawSavedLang = this.platformService.isServer
      ? null
      : localStorage.getItem(environment.languages.saveKey);

    const savedLang = this.getSafeLang(rawSavedLang);

    const defaultLang = this.translate.getDefaultLang();
    const currentUrl = this.location.path();
    const arrayOfUrlParts = currentUrl.indexOf("/") !== -1 ? currentUrl.split("/") || [] : currentUrl ? [currentUrl] : [];
    const isEnInURL = arrayOfUrlParts.includes("en");
    const isArInURL = arrayOfUrlParts.includes("ar");

    if (isEnInURL) return 'en';
    if (isArInURL) return 'ar';
    if (savedLang) return savedLang;
    return defaultLang || 'en';
  }

  isRTL() {
    return this.getSelectedLanguage() === "ar";
  }

}
